import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { ScrollArea } from '../../components/ui/scroll-area';
import { 
  User, MapPin, Calendar as CalendarIcon, Package, CheckCircle2,
  ArrowRight, ArrowLeft, Camera, Sparkles, Plus, Trash2,
  AlertTriangle, Home, Truck, Check
} from 'lucide-react';
import { format } from 'date-fns';
import api from '../../utils/api';
import AddressAutocomplete from '../../components/AddressAutocomplete';

const STEPS = [
  { id: 'profile', title: 'Your Details', icon: User },
  { id: 'move-from', title: 'Moving From', icon: Home },
  { id: 'move-to', title: 'Moving To', icon: Truck },
  { id: 'dates', title: 'Move Dates', icon: CalendarIcon },
  { id: 'inventory', title: 'Your Items', icon: Package },
  { id: 'review', title: 'Review', icon: CheckCircle2 },
];

const CustomerSetupWizard = ({ user, setUser, job }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showAIScanner, setShowAIScanner] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    // Profile (pre-filled by company)
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    
    // Move-out address
    from_street: job?.from_address?.street || '',
    from_city: job?.from_address?.city || '',
    from_postal_code: job?.from_address?.postal_code || '',
    from_country: job?.from_address?.country || 'UK',
    from_property_type: '',
    from_floor: '',
    from_lift: false,
    from_parking: '',
    from_notes: '',
    
    // Move-in address
    to_street: job?.to_address?.street || '',
    to_city: job?.to_address?.city || '',
    to_postal_code: job?.to_address?.postal_code || '',
    to_country: job?.to_address?.country || 'UK',
    to_property_type: '',
    to_floor: '',
    to_lift: false,
    to_parking: '',
    to_notes: '',
    
    // Dates
    move_out_date: job?.move_out_date ? new Date(job.move_out_date) : null,
    move_in_date: job?.move_in_date ? new Date(job.move_in_date) : null,
    flexible_dates: false,
    preferred_time: 'morning',
    
    // Inventory
    inventory_items: [],
  });
  
  // Inventory templates
  const [inventoryTemplates, setInventoryTemplates] = useState([]);
  const [inventoryCategories, setInventoryCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showItemPicker, setShowItemPicker] = useState(false);

  useEffect(() => {
    fetchInventoryTemplates();
  }, []);

  const fetchInventoryTemplates = async () => {
    try {
      const response = await api.get('/inventory/templates?item_type=domestic');
      setInventoryTemplates(response.data.items);
      setInventoryCategories(response.data.categories.domestic);
    } catch (error) {
      console.error('Failed to load templates');
    }
  };

  const updateFormData = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const addInventoryItem = (template) => {
    const existingIndex = formData.inventory_items.findIndex(
      i => i.item_name === template.name
    );
    
    if (existingIndex >= 0) {
      // Increment quantity
      const items = [...formData.inventory_items];
      items[existingIndex].quantity += 1;
      updateFormData('inventory_items', items);
    } else {
      // Add new item
      const newItem = {
        id: `temp-${Date.now()}`,
        item_name: template.name,
        category: template.category,
        quantity: 1,
        is_fragile: template.is_fragile,
        dimensions: template.dimensions,
        notes: '',
      };
      updateFormData('inventory_items', [...formData.inventory_items, newItem]);
    }
    toast.success(`Added ${template.name}`);
  };

  const removeInventoryItem = (index) => {
    const items = formData.inventory_items.filter((_, i) => i !== index);
    updateFormData('inventory_items', items);
  };

  const updateItemQuantity = (index, quantity) => {
    const items = [...formData.inventory_items];
    items[index].quantity = Math.max(1, quantity);
    updateFormData('inventory_items', items);
  };

  const handleNext = () => {
    // Validate current step
    if (currentStep === 0) {
      if (!formData.first_name || !formData.last_name) {
        toast.error('Please fill in your name');
        return;
      }
    } else if (currentStep === 1) {
      if (!formData.from_street || !formData.from_city || !formData.from_postal_code) {
        toast.error('Please fill in the move-out address');
        return;
      }
    } else if (currentStep === 2) {
      if (!formData.to_street || !formData.to_city || !formData.to_postal_code) {
        toast.error('Please fill in the move-in address');
        return;
      }
    } else if (currentStep === 3) {
      if (!formData.move_out_date) {
        toast.error('Please select a move-out date');
        return;
      }
    }
    
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    
    try {
      // Update user profile
      await api.put(`/users/${user.id}`, {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone: formData.phone,
        profile_completed: true,
      });
      
      // Create or update job
      const jobData = {
        from_address: {
          street: formData.from_street,
          city: formData.from_city,
          postal_code: formData.from_postal_code,
          country: formData.from_country,
          additional_info: formData.from_notes,
        },
        to_address: {
          street: formData.to_street,
          city: formData.to_city,
          postal_code: formData.to_postal_code,
          country: formData.to_country,
          additional_info: formData.to_notes,
        },
        move_out_date: formData.move_out_date?.toISOString(),
        move_in_date: formData.move_in_date?.toISOString(),
        property_details: {
          from_property_type: formData.from_property_type,
          from_floor: formData.from_floor,
          from_lift: formData.from_lift,
          from_parking: formData.from_parking,
          to_property_type: formData.to_property_type,
          to_floor: formData.to_floor,
          to_lift: formData.to_lift,
          to_parking: formData.to_parking,
        },
        flexible_dates: formData.flexible_dates,
        preferred_time: formData.preferred_time,
      };
      
      let jobId = job?.id;
      
      if (jobId) {
        await api.put(`/jobs/${jobId}`, jobData);
      } else {
        const jobResponse = await api.post('/jobs', jobData);
        jobId = jobResponse.data.id;
      }
      
      // Add inventory items
      for (const item of formData.inventory_items) {
        await api.post('/inventory', {
          job_id: jobId,
          category: item.category,
          item_name: item.item_name,
          quantity: item.quantity,
          is_fragile: item.is_fragile,
          notes: item.notes,
        });
      }
      
      // Update local user state
      setUser({ ...user, profile_completed: true });
      localStorage.setItem('movemate_user', JSON.stringify({ ...user, profile_completed: true }));
      
      toast.success('Setup complete! Your moving details have been saved.');
      navigate('/mobile/dashboard');
      
    } catch (error) {
      toast.error('Failed to save. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const filteredTemplates = inventoryTemplates.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const progress = ((currentStep + 1) / STEPS.length) * 100;
  const CurrentStepIcon = STEPS[currentStep].icon;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50" data-testid="customer-setup-wizard">
      {/* Header */}
      <header className="bg-white border-b shadow-sm sticky top-0 z-10">
        <div className="px-4 py-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="MoveMate" className="h-8" />
              <div>
                <h1 className="text-lg font-bold gradient-text">Setup Your Move</h1>
                <p className="text-xs text-gray-500">Step {currentStep + 1} of {STEPS.length}</p>
              </div>
            </div>
            <Badge variant="outline">
              <CurrentStepIcon size={14} className="mr-1" />
              {STEPS[currentStep].title}
            </Badge>
          </div>
          <Progress value={progress} className="h-2" />
        </div>
      </header>

      {/* Step Content */}
      <main className="p-4 pb-32">
        {/* Step 1: Profile */}
        {currentStep === 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="text-primary" size={24} />
                Confirm Your Details
              </CardTitle>
              <CardDescription>
                Please verify the information provided by your moving company
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>First Name *</Label>
                  <Input
                    value={formData.first_name}
                    onChange={(e) => updateFormData('first_name', e.target.value)}
                    data-testid="first-name-input"
                  />
                </div>
                <div>
                  <Label>Last Name *</Label>
                  <Input
                    value={formData.last_name}
                    onChange={(e) => updateFormData('last_name', e.target.value)}
                    data-testid="last-name-input"
                  />
                </div>
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => updateFormData('email', e.target.value)}
                  placeholder="your@email.com"
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => updateFormData('phone', e.target.value)}
                  placeholder="+44 7700 900000"
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Move From Address */}
        {currentStep === 1 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Home className="text-primary" size={24} />
                Where are you moving from?
              </CardTitle>
              <CardDescription>
                Your current address for collection
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Search Address *</Label>
                <AddressAutocomplete
                  value={formData.from_formatted_address || ''}
                  onChange={(val) => updateFormData('from_formatted_address', val)}
                  onPlaceSelect={(place) => {
                    updateFormData('from_street', place.street);
                    updateFormData('from_city', place.city);
                    updateFormData('from_postal_code', place.postal_code);
                    updateFormData('from_country', place.country || 'UK');
                    updateFormData('from_formatted_address', place.formatted_address);
                    updateFormData('from_lat', place.lat);
                    updateFormData('from_lng', place.lng);
                  }}
                  placeholder="Start typing your address..."
                />
              </div>
              
              {/* Show parsed address fields */}
              {formData.from_street && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm font-medium text-green-800 mb-2">Address found:</p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-gray-500">Street:</span>
                      <p className="font-medium">{formData.from_street}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">City:</span>
                      <p className="font-medium">{formData.from_city}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Postal Code:</span>
                      <p className="font-medium">{formData.from_postal_code}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Country:</span>
                      <p className="font-medium">{formData.from_country}</p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Manual entry fallback */}
              <details className="text-sm">
                <summary className="cursor-pointer text-gray-500 hover:text-gray-700">
                  Enter address manually
                </summary>
                <div className="mt-3 space-y-3 pt-3 border-t">
                  <div>
                    <Label>Street Address</Label>
                    <Input
                      value={formData.from_street}
                      onChange={(e) => updateFormData('from_street', e.target.value)}
                      placeholder="123 Example Street"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>City</Label>
                      <Input
                        value={formData.from_city}
                        onChange={(e) => updateFormData('from_city', e.target.value)}
                        placeholder="London"
                      />
                    </div>
                    <div>
                      <Label>Postal Code</Label>
                      <Input
                        value={formData.from_postal_code}
                        onChange={(e) => updateFormData('from_postal_code', e.target.value)}
                        placeholder="SW1A 1AA"
                      />
                    </div>
                  </div>
                </div>
              </details>
              
              <div>
                <Label>Property Type</Label>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {['House', 'Flat', 'Office'].map(type => (
                    <Button
                      key={type}
                      variant={formData.from_property_type === type ? 'default' : 'outline'}
                      className="h-12"
                      onClick={() => updateFormData('from_property_type', type)}
                    >
                      {type}
                    </Button>
                  ))}
                </div>
              </div>
              {formData.from_property_type === 'Flat' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Floor</Label>
                    <Input
                      value={formData.from_floor}
                      onChange={(e) => updateFormData('from_floor', e.target.value)}
                      placeholder="e.g. 3rd"
                    />
                  </div>
                  <div>
                    <Label>Lift Available?</Label>
                    <div className="flex gap-2 mt-2">
                      <Button
                        variant={formData.from_lift ? 'default' : 'outline'}
                        onClick={() => updateFormData('from_lift', true)}
                        className="flex-1"
                      >
                        Yes
                      </Button>
                      <Button
                        variant={!formData.from_lift ? 'default' : 'outline'}
                        onClick={() => updateFormData('from_lift', false)}
                        className="flex-1"
                      >
                        No
                      </Button>
                    </div>
                  </div>
                </div>
              )}
              <div>
                <Label>Parking Information</Label>
                <Textarea
                  value={formData.from_parking}
                  onChange={(e) => updateFormData('from_parking', e.target.value)}
                  placeholder="e.g. Street parking, permit required"
                  rows={2}
                />
              </div>
              <div>
                <Label>Additional Notes</Label>
                <Textarea
                  value={formData.from_notes}
                  onChange={(e) => updateFormData('from_notes', e.target.value)}
                  placeholder="Any special instructions for collection..."
                  rows={2}
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Move To Address */}
        {currentStep === 2 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Truck className="text-primary" size={24} />
                Where are you moving to?
              </CardTitle>
              <CardDescription>
                Your new address for delivery
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Search Address *</Label>
                <AddressAutocomplete
                  value={formData.to_formatted_address || ''}
                  onChange={(val) => updateFormData('to_formatted_address', val)}
                  onPlaceSelect={(place) => {
                    updateFormData('to_street', place.street);
                    updateFormData('to_city', place.city);
                    updateFormData('to_postal_code', place.postal_code);
                    updateFormData('to_country', place.country || 'UK');
                    updateFormData('to_formatted_address', place.formatted_address);
                    updateFormData('to_lat', place.lat);
                    updateFormData('to_lng', place.lng);
                  }}
                  placeholder="Start typing your new address..."
                />
              </div>
              
              {/* Show parsed address fields */}
              {formData.to_street && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm font-medium text-green-800 mb-2">Address found:</p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-gray-500">Street:</span>
                      <p className="font-medium">{formData.to_street}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">City:</span>
                      <p className="font-medium">{formData.to_city}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Postal Code:</span>
                      <p className="font-medium">{formData.to_postal_code}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Country:</span>
                      <p className="font-medium">{formData.to_country}</p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Manual entry fallback */}
              <details className="text-sm">
                <summary className="cursor-pointer text-gray-500 hover:text-gray-700">
                  Enter address manually
                </summary>
                <div className="mt-3 space-y-3 pt-3 border-t">
                  <div>
                    <Label>Street Address</Label>
                    <Input
                      value={formData.to_street}
                      onChange={(e) => updateFormData('to_street', e.target.value)}
                      placeholder="456 New Street"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>City</Label>
                      <Input
                        value={formData.to_city}
                        onChange={(e) => updateFormData('to_city', e.target.value)}
                        placeholder="Manchester"
                      />
                    </div>
                    <div>
                      <Label>Postal Code</Label>
                      <Input
                        value={formData.to_postal_code}
                        onChange={(e) => updateFormData('to_postal_code', e.target.value)}
                        placeholder="M1 1AA"
                      />
                    </div>
                  </div>
                </div>
              </details>
              
              <div>
                <Label>Property Type</Label>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {['House', 'Flat', 'Office'].map(type => (
                    <Button
                      key={type}
                      variant={formData.to_property_type === type ? 'default' : 'outline'}
                      className="h-12"
                      onClick={() => updateFormData('to_property_type', type)}
                    >
                      {type}
                    </Button>
                  ))}
                </div>
              </div>
              {formData.to_property_type === 'Flat' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Floor</Label>
                    <Input
                      value={formData.to_floor}
                      onChange={(e) => updateFormData('to_floor', e.target.value)}
                      placeholder="e.g. 5th"
                    />
                  </div>
                  <div>
                    <Label>Lift Available?</Label>
                    <div className="flex gap-2 mt-2">
                      <Button
                        variant={formData.to_lift ? 'default' : 'outline'}
                        onClick={() => updateFormData('to_lift', true)}
                        className="flex-1"
                      >
                        Yes
                      </Button>
                      <Button
                        variant={!formData.to_lift ? 'default' : 'outline'}
                        onClick={() => updateFormData('to_lift', false)}
                        className="flex-1"
                      >
                        No
                      </Button>
                    </div>
                  </div>
                </div>
              )}
              <div>
                <Label>Parking Information</Label>
                <Textarea
                  value={formData.to_parking}
                  onChange={(e) => updateFormData('to_parking', e.target.value)}
                  placeholder="e.g. Driveway available"
                  rows={2}
                />
              </div>
              <div>
                <Label>Additional Notes</Label>
                <Textarea
                  value={formData.to_notes}
                  onChange={(e) => updateFormData('to_notes', e.target.value)}
                  placeholder="Any special instructions for delivery..."
                  rows={2}
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 4: Move Dates */}
        {currentStep === 3 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CalendarIcon className="text-primary" size={24} />
                When are you moving?
              </CardTitle>
              <CardDescription>
                Select your preferred moving dates
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label className="text-base font-semibold">Move-Out Date *</Label>
                <p className="text-sm text-gray-500 mb-2">When should we collect your items?</p>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left h-12">
                      <CalendarIcon className="mr-2" size={18} />
                      {formData.move_out_date 
                        ? format(formData.move_out_date, 'PPP')
                        : 'Select date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={formData.move_out_date}
                      onSelect={(date) => updateFormData('move_out_date', date)}
                      disabled={(date) => date < new Date()}
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div>
                <Label className="text-base font-semibold">Move-In Date</Label>
                <p className="text-sm text-gray-500 mb-2">When should we deliver? (Optional)</p>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left h-12">
                      <CalendarIcon className="mr-2" size={18} />
                      {formData.move_in_date 
                        ? format(formData.move_in_date, 'PPP')
                        : 'Same day or select different date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={formData.move_in_date}
                      onSelect={(date) => updateFormData('move_in_date', date)}
                      disabled={(date) => date < (formData.move_out_date || new Date())}
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div>
                <Label>Preferred Time</Label>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {[
                    { value: 'morning', label: 'Morning', desc: '8am-12pm' },
                    { value: 'afternoon', label: 'Afternoon', desc: '12pm-5pm' },
                    { value: 'flexible', label: 'Flexible', desc: 'Any time' },
                  ].map(time => (
                    <Button
                      key={time.value}
                      variant={formData.preferred_time === time.value ? 'default' : 'outline'}
                      className="h-16 flex-col"
                      onClick={() => updateFormData('preferred_time', time.value)}
                    >
                      <span className="font-semibold">{time.label}</span>
                      <span className="text-xs opacity-75">{time.desc}</span>
                    </Button>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                <input
                  type="checkbox"
                  id="flexible"
                  checked={formData.flexible_dates}
                  onChange={(e) => updateFormData('flexible_dates', e.target.checked)}
                  className="w-5 h-5"
                />
                <label htmlFor="flexible" className="text-sm">
                  My dates are flexible - contact me with available options
                </label>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 5: Inventory */}
        {currentStep === 4 && (
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package className="text-primary" size={24} />
                  What are you moving?
                </CardTitle>
                <CardDescription>
                  Add items to help us provide an accurate quote
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Add Methods */}
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    variant="outline"
                    className="h-20 flex-col"
                    onClick={() => setShowItemPicker(true)}
                    data-testid="add-from-list-btn"
                  >
                    <Plus size={24} className="mb-1" />
                    <span className="text-sm">From List</span>
                  </Button>
                  <Button
                    variant="outline"
                    className="h-20 flex-col"
                    onClick={() => setShowAIScanner(true)}
                    data-testid="ai-scan-btn"
                  >
                    <Sparkles size={24} className="mb-1" />
                    <span className="text-sm">AI Scanner</span>
                  </Button>
                </div>

                {/* Current Items */}
                {formData.inventory_items.length > 0 ? (
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <Label>Your Items ({formData.inventory_items.length})</Label>
                      <span className="text-sm text-gray-500">
                        {formData.inventory_items.reduce((sum, i) => sum + i.quantity, 0)} total
                      </span>
                    </div>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {formData.inventory_items.map((item, idx) => (
                        <div 
                          key={item.id} 
                          className={`p-3 border rounded-lg flex items-center justify-between ${
                            item.is_fragile ? 'bg-orange-50 border-orange-200' : ''
                          }`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-sm truncate">{item.item_name}</p>
                              {item.is_fragile && (
                                <AlertTriangle size={14} className="text-orange-500 shrink-0" />
                              )}
                            </div>
                            <p className="text-xs text-gray-500">{item.category}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => updateItemQuantity(idx, item.quantity - 1)}
                            >
                              -
                            </Button>
                            <span className="w-8 text-center font-semibold">{item.quantity}</span>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => updateItemQuantity(idx, item.quantity + 1)}
                            >
                              +
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeInventoryItem(idx)}
                            >
                              <Trash2 size={16} className="text-red-500" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Package size={48} className="mx-auto mb-2 opacity-50" />
                    <p>No items added yet</p>
                    <p className="text-xs mt-1">Tap a button above to add items</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Step 6: Review */}
        {currentStep === 5 && (
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle2 className="text-green-600" size={24} />
                  Review Your Details
                </CardTitle>
                <CardDescription>
                  Please check everything is correct before submitting
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Profile Summary */}
                <div>
                  <h4 className="font-semibold flex items-center gap-2 mb-2">
                    <User size={16} /> Your Details
                  </h4>
                  <div className="bg-gray-50 p-3 rounded-lg text-sm">
                    <p>{formData.first_name} {formData.last_name}</p>
                    {formData.email && <p className="text-gray-500">{formData.email}</p>}
                    {formData.phone && <p className="text-gray-500">{formData.phone}</p>}
                  </div>
                </div>

                {/* Addresses Summary */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-semibold flex items-center gap-2 mb-2">
                      <Home size={16} /> Moving From
                    </h4>
                    <div className="bg-gray-50 p-3 rounded-lg text-sm">
                      <p>{formData.from_street}</p>
                      <p>{formData.from_city}, {formData.from_postal_code}</p>
                      {formData.from_property_type && (
                        <Badge variant="outline" className="mt-2">{formData.from_property_type}</Badge>
                      )}
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold flex items-center gap-2 mb-2">
                      <Truck size={16} /> Moving To
                    </h4>
                    <div className="bg-gray-50 p-3 rounded-lg text-sm">
                      <p>{formData.to_street}</p>
                      <p>{formData.to_city}, {formData.to_postal_code}</p>
                      {formData.to_property_type && (
                        <Badge variant="outline" className="mt-2">{formData.to_property_type}</Badge>
                      )}
                    </div>
                  </div>
                </div>

                {/* Dates Summary */}
                <div>
                  <h4 className="font-semibold flex items-center gap-2 mb-2">
                    <CalendarIcon size={16} /> Move Dates
                  </h4>
                  <div className="bg-gray-50 p-3 rounded-lg text-sm">
                    <p>
                      <strong>Collection:</strong>{' '}
                      {formData.move_out_date ? format(formData.move_out_date, 'PPP') : 'Not set'}
                    </p>
                    {formData.move_in_date && (
                      <p>
                        <strong>Delivery:</strong> {format(formData.move_in_date, 'PPP')}
                      </p>
                    )}
                    <p className="text-gray-500 mt-1">
                      Preferred: {formData.preferred_time === 'morning' ? 'Morning (8am-12pm)' : 
                                 formData.preferred_time === 'afternoon' ? 'Afternoon (12pm-5pm)' : 'Flexible'}
                    </p>
                  </div>
                </div>

                {/* Inventory Summary */}
                <div>
                  <h4 className="font-semibold flex items-center gap-2 mb-2">
                    <Package size={16} /> Your Items
                  </h4>
                  <div className="bg-gray-50 p-3 rounded-lg text-sm">
                    <p className="font-medium">
                      {formData.inventory_items.reduce((sum, i) => sum + i.quantity, 0)} items added
                    </p>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {formData.inventory_items.slice(0, 5).map((item, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs">
                          {item.quantity}x {item.item_name}
                        </Badge>
                      ))}
                      {formData.inventory_items.length > 5 && (
                        <Badge variant="secondary" className="text-xs">
                          +{formData.inventory_items.length - 5} more
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </main>

      {/* Fixed Footer Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t p-4 safe-area-bottom">
        <div className="flex gap-3">
          {currentStep > 0 && (
            <Button variant="outline" onClick={handleBack} className="flex-1 h-12">
              <ArrowLeft size={18} className="mr-2" />
              Back
            </Button>
          )}
          {currentStep < STEPS.length - 1 ? (
            <Button onClick={handleNext} className="flex-1 h-12" data-testid="next-step-btn">
              Next
              <ArrowRight size={18} className="ml-2" />
            </Button>
          ) : (
            <Button 
              onClick={handleSubmit} 
              className="flex-1 h-12 bg-green-600 hover:bg-green-700"
              disabled={loading}
              data-testid="submit-btn"
            >
              {loading ? 'Saving...' : (
                <>
                  <Check size={18} className="mr-2" />
                  Complete Setup
                </>
              )}
            </Button>
          )}
        </div>
      </div>

      {/* Item Picker Dialog */}
      <Dialog open={showItemPicker} onOpenChange={setShowItemPicker}>
        <DialogContent className="max-w-lg max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Add Items</DialogTitle>
            <DialogDescription>Select items from the list</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3">
            <Input
              placeholder="Search items..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            
            {/* Horizontal Category Scroll */}
            <ScrollArea className="w-full whitespace-nowrap">
              <div className="flex gap-2 pb-2" data-testid="category-scroll">
                <Badge
                  variant={selectedCategory === 'all' ? 'default' : 'outline'}
                  className="cursor-pointer shrink-0"
                  onClick={() => setSelectedCategory('all')}
                >
                  All
                </Badge>
                {inventoryCategories.map((cat) => (
                  <Badge
                    key={cat}
                    variant={selectedCategory === cat ? 'default' : 'outline'}
                    className="cursor-pointer shrink-0"
                    onClick={() => setSelectedCategory(cat)}
                  >
                    {cat}
                  </Badge>
                ))}
              </div>
            </ScrollArea>
            
            <ScrollArea className="h-[300px]">
              <div className="space-y-2 pr-4">
                {filteredTemplates.slice(0, 50).map((item, idx) => (
                  <div
                    key={`${item.name}-${idx}`}
                    className="p-3 border rounded-lg flex justify-between items-center hover:bg-gray-50 cursor-pointer"
                    onClick={() => addInventoryItem(item)}
                  >
                    <div>
                      <p className="font-medium text-sm">{item.name}</p>
                      <p className="text-xs text-gray-500">{item.category}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {item.is_fragile && (
                        <Badge variant="destructive" className="text-xs">Fragile</Badge>
                      )}
                      <Plus size={18} className="text-primary" />
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>

          <Button variant="outline" onClick={() => setShowItemPicker(false)}>
            Done
          </Button>
        </DialogContent>
      </Dialog>

      {/* AI Scanner would redirect to AIScanner page */}
      <Dialog open={showAIScanner} onOpenChange={setShowAIScanner}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="text-primary" />
              AI Inventory Scanner
            </DialogTitle>
            <DialogDescription>
              Take a photo of your room and our AI will identify items
            </DialogDescription>
          </DialogHeader>
          
          <div className="text-center py-8">
            <Camera size={64} className="mx-auto text-gray-400 mb-4" />
            <p className="text-gray-600 mb-4">
              The AI scanner analyzes photos to identify furniture and household items automatically.
            </p>
            <Button onClick={() => {
              setShowAIScanner(false);
              navigate('/ai-scanner');
            }}>
              <Camera size={18} className="mr-2" />
              Open Scanner
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomerSetupWizard;
